import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { USER_ROLES } from "../const";

export const NAVIGATION_CONFIG = {
  super_admin: [
    { path: "/super-admin", label: "Platform Dashboard", icon: "" },
    { path: "/super-admin/billing", label: "Billing", icon: "" },
  ],
  admin: [
    { path: "/", label: "Home", icon: "" },
    { path: "/recipes", label: "Recipes", icon: "" },
    { path: "/groups", label: "Targeting", icon: "" },
    { path: "/feedback-reports", label: "Voting Reports", icon: "" },
    { path: "/admin/billing", label: "Billing", icon: "" },
    { path: "/admin", label: "Users", icon: "" },
  ],
  user: [
    { path: "/", label: "Home", icon: "" },
    { path: "/recipes", label: "Recipes", icon: "" },
    { path: "/groups", label: "Targeting", icon: "" },
    { path: "/feedback-reports", label: "Voting Reports", icon: "" },
  ],
};

const isDirectCulmenUser = (user) =>
  user?.company?.isCulmen === true;

// Sub-pages keep their section's nav item selected, so /admin/billing/history
// still shows Billing as the current page.
const SECTION_PATHS = ["/recipes", "/admin/billing"];

export const isNavItemActive = (pathname, path) => {
  if (pathname === path) return true;

  return SECTION_PATHS.includes(path) && pathname.startsWith(`${path}/`);
};

export const getNavigationItems = (user) => {
  const role = user?.role?.toLowerCase?.();

  if (role === USER_ROLES.SUPER_ADMIN) {
    return NAVIGATION_CONFIG.super_admin || [];
  }

  if (role === USER_ROLES.ADMIN) {
    const adminItems = NAVIGATION_CONFIG.admin || [];

    return isDirectCulmenUser(user)
      ? adminItems.filter((item) => item.path !== "/admin/billing")
      : adminItems;
  }

  return NAVIGATION_CONFIG.user || [];
};

// Special admin-only routes
const SPECIAL_ADMIN_ROUTES = {
  "/admin/control-pannel": { icon: "", label: "Control Panel" },
};

// Common styling classes
const STYLES = {
  navLink: (isActive, isMobile = false) => `
    px-4 py-2 rounded-lg transition-colors duration-300 text-sm flex items-center
    ${
      isActive
        ? "bg-brand-yellow text-brand-ink"
        : isMobile
          ? "text-gray-700 hover:bg-brand-gold-soft hover:text-brand-gold"
          : "text-white hover:bg-brand-gold-soft hover:text-brand-gold"
    }
  `,
  navLinkSpecial: (isActive) => `
    px-2 py-2 rounded-lg transition-colors duration-300 flex items-center 
    ${
      isActive
        ? "bg-brand-yellow text-brand-ink"
        : "text-white hover:bg-brand-gold-soft hover:text-brand-gold"
    }
  `,
  iconButton: (isActive) => `
    flex items-center justify-center p-2 rounded-lg transition-colors duration-300
    ${
      isActive
        ? "bg-brand-yellow text-brand-ink"
        : "text-white hover:bg-brand-gold-soft hover:text-brand-gold"
    }
  `,
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuth();
  const role = user?.role?.toLowerCase?.();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActive = (path) => isNavItemActive(location.pathname, path);

  const renderNavLink = (item, isMobile = false) => (
    <Link
      key={item.path}
      to={item.path}
      onClick={isMobile ? closeMenu : undefined}
      className={STYLES.navLink(isActive(item.path), isMobile)}
    >
      {item.icon} {item.label}
    </Link>
  );

  const renderSpecialAdminLink = (path, config, isMobile = false) => {
    const isRouteActive = isActive(path);

    return (
      <Link
        key={path}
        to={path}
        onClick={isMobile ? closeMenu : undefined}
        className={STYLES.navLinkSpecial(isRouteActive)}
      >
        <i className={`bi ${config.icon}`}></i>
        <span className="ml-1 text-sm">{config.label}</span>
      </Link>
    );
  };

  // Settings icon with tooltip for desktop
  const SettingsIcon = ({ isMobile = false }) => {
    const isProfileActive = isActive("/user-profile");

    return (
      <Link
        to="/user-profile"
        onClick={isMobile ? closeMenu : undefined}
        className={
          isMobile ? STYLES.navLink(isProfileActive, true) : "group relative"
        }
        title="User Profile & Settings"
      >
        {isMobile ? (
          <>
            <i className="bi bi-gear"></i>
            <span>Settings & Profile</span>
          </>
        ) : (
          <>
            <div className={STYLES.iconButton(isProfileActive)}>
              <i className="bi bi-gear text-lg"></i>
            </div>
            {/* Tooltip for desktop */}
          </>
        )}
      </Link>
    );
  };

  const renderDesktopNavLinks = () => (
    <div className="hidden lg:flex items-center space-x-2 ml-4">
      {getNavigationItems(user).map((item) => renderNavLink(item))}

      {role === USER_ROLES.ADMIN && user?.company?.name === "SPNTL" && (
        <>
          {renderSpecialAdminLink(
            "/admin/control-pannel",
            SPECIAL_ADMIN_ROUTES["/admin/control-pannel"],
          )}
        </>
      )}
    </div>
  );

  const renderDesktopActions = () => (
    <div className="hidden lg:flex items-center space-x-2 ml-auto">
      {isAuthenticated && (
        <button
          onClick={logout}
          className="px-4 py-2 rounded-lg text-sm transition-colors duration-300 text-white hover:bg-red-600 hover:text-white flex items-center gap-2"
          title="Logout"
        >
          <i className="bi bi-box-arrow-right"></i>
          <span className="hidden md:inline">Logout</span>
        </button>
      )}
      {isAuthenticated && <SettingsIcon />}
    </div>
  );

  const renderMobileNavigation = () => (
    <div className="lg:hidden pb-4">
      <div className="flex flex-col space-y-2">
        {/* Regular navigation items */}
        {getNavigationItems(user).map((item) => renderNavLink(item, true))}

        {/* Special admin-only routes */}
        {(role === USER_ROLES.SUPER_ADMIN ||
          (role === USER_ROLES.ADMIN && user?.company?.name === "SPNTL")) && (
          <>
            {renderSpecialAdminLink(
              "/admin/control-pannel",
              SPECIAL_ADMIN_ROUTES["/admin/control-pannel"],
              true,
            )}
          </>
        )}

        {/* Settings link for mobile */}
        {isAuthenticated && <SettingsIcon isMobile={true} />}

        {/* Authentication section */}
        {isAuthenticated && (
          <button
            onClick={() => {
              logout();
              closeMenu();
            }}
            className="w-full text-left px-4 py-2 rounded-lg transition-colors duration-300 text-white hover:bg-red-600 hover:text-white flex items-center gap-2"
          >
            <i className="bi bi-box-arrow-right"></i>
            <span>Logout</span>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <nav className="bg-gray-900 shadow-lg border-b-2 border-brand-yellow sticky top-0 z-50">
      <div className=" mx-auto px-4">
        <div className="flex items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-xl font-bold text-white">
              <img
                src="/logo/logoWhite.png"
                alt="logo"
                width={200}
                height={200}
              />
            </span>
          </Link>

          {renderDesktopNavLinks()}

          {renderDesktopActions()}

          <div className="lg:hidden">
            <button
              onClick={toggleMenu}
              className="text-white hover:text-brand-gold focus:outline-none focus:text-brand-gold"
            >
              <i
                className={`bi ${isMenuOpen ? "bi-x" : "bi-list"} text-2xl`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && renderMobileNavigation()}
      </div>
    </nav>
  );
};

export default Navbar;
